import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarPlus, Flame, ListFilter } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';
import StudentAvatar from '../../components/Student360/StudentAvatar';
import PresenceBadge, { LastLogin } from '../../components/Student360/PresenceBadge';
import ProgressBar from '../../components/Student360/ProgressBar';
import ContactLinks from '../../components/Student360/ContactLinks';
import LessonLine from '../../components/Student360/LessonLine';
import { AccountStatusBadge, AtRiskChip, EnrollmentBadge } from '../../components/Student360/StatusChips';
import { STUDENT_TIME_ZONE } from '../../components/Student360/time';
import { formatDate } from '../../utils/datetime';

/**
 * Which columns the response has fields for. They come from `meta` (the groups the server granted,
 * the presence precision, the learning detail), never from the viewer's role, so an empty page
 * still has the right header and a role the server adds tomorrow needs no change here.
 *
 * `streak` is not a column of its own: it is the flame that sits under the last-seen words, drawn
 * when the response carries the PR group.
 */
export function columnsFor(meta) {
  const groups = new Set(meta?.groups || ['ID']);
  return {
    contact: groups.has('CT'),
    learning: groups.has('LR'),
    lesson: groups.has('LR') && meta?.learning === 'full',
    presence: groups.has('PS'),
    streak: groups.has('PR'),
  };
}

/**
 * The course in focus, its progress and, for an enrollment that is not simply "active", the state in
 * place of the percentage. Two lines at most: the other courses of the student are a "+4" count with
 * the sentence in its tooltip.
 */
function ProgressCell({ row, t, onPickCourse }) {
  const { focus } = row;
  if (!focus) return <span className="s360-muted">{t('student360.notEnrolled')}</span>;
  const more = Number(row.enrollmentCount) - 1;
  const open = focus.status === 'ACTIVE';
  return (
    <div className="sdir-progress">
      <div className="sdir-progress-top">
        {onPickCourse ? (
          <button
            type="button"
            className="sdir-course is-link"
            title={t('student360.directory.filterByCourse')}
            onClick={() => onPickCourse(focus.courseId)}
          >
            <span className="sdir-course-title">{focus.title}</span>
            <ListFilter aria-hidden="true" />
          </button>
        ) : (
          <span className="sdir-course" title={focus.title}>
            <span className="sdir-course-title">{focus.title}</span>
          </span>
        )}
        {more > 0 && (
          <span className="sdir-more" title={t('student360.moreCourses', { count: more })}>{`+${more}`}</span>
        )}
      </div>
      <div className="sdir-progress-bar">
        <ProgressBar value={focus.progressPercent} label={focus.title} showValue={open} />
        {!open && <EnrollmentBadge status={focus.status} />}
      </div>
    </div>
  );
}

function DirectoryRow({
  row, columns, now, lang, detailPath, onOpen, onPickCourse,
}) {
  const { t } = useI18n();

  // The whole row opens the student; the links and buttons inside keep their own meaning.
  const handleClick = (event) => {
    if (event.target.closest('a, button')) return;
    onOpen(detailPath);
  };

  return (
    <tr className="sdir-row is-clickable" onClick={handleClick}>
      <td className="sdir-cell-student">
        <div className="sdir-person">
          <Link className="ui-cell-person sdir-student" to={detailPath}>
            <StudentAvatar
              name={row.name}
              avatar={row.avatar}
              seed={row.email || row.id}
              online={row.presence?.onlineNow === true}
            />
            <span className="ui-cell-person__text">
              <span className="ui-cell-person__name">{row.name}</span>
              <span
                className="ui-cell-person__sub sdir-joined"
                title={t('student360.joinedOn', { date: formatDate(row.joinedAt, { lang, timeZone: STUDENT_TIME_ZONE }) })}
              >
                <CalendarPlus aria-hidden="true" />
                <span>{formatDate(row.joinedAt, { lang, timeZone: STUDENT_TIME_ZONE })}</span>
              </span>
            </span>
          </Link>
          <AccountStatusBadge status={row.accountStatus} />
        </div>
      </td>
      {columns.contact && (
        <td className="sdir-cell-contact" data-label={t('student360.directory.cols.contact')}>
          <ContactLinks phone={row.phone} email={row.email} />
        </td>
      )}
      {columns.learning && (
        <td className="sdir-cell-progress" data-label={t('student360.directory.cols.progress')}>
          <ProgressCell row={row} t={t} onPickCourse={onPickCourse} />
        </td>
      )}
      {columns.lesson && (
        <td className="sdir-cell-lesson" data-label={t('student360.directory.cols.lesson')}>
          {row.focus ? <LessonLine lesson={row.focus.currentLesson} now={now} compact /> : <span className="s360-muted">—</span>}
        </td>
      )}
      {columns.presence && (
        <td className="sdir-cell-seen" data-label={t('student360.directory.cols.lastSeen')}>
          <div className="sdir-seen">
            <div className="sdir-seen-top">
              <PresenceBadge presence={row.presence} now={now} />
              {row.atRisk === true && <AtRiskChip />}
            </div>
            <div className="sdir-seen-sub">
              <LastLogin presence={row.presence} now={now} labelled />
              {columns.streak && Number.isFinite(row.streak) && (
                <span
                  className={`s360-streak ${row.streak > 0 ? 'is-lit' : ''}`}
                  title={t('student360.directory.cols.streak')}
                >
                  <Flame size={13} aria-hidden="true" />
                  {row.streak}
                </span>
              )}
            </div>
          </div>
        </td>
      )}
    </tr>
  );
}

/**
 * The rows of one page of the Directory. `courseId` is the course filter in force: it travels to
 * the student page so a click from "Russian A1" opens on that course. `onPickCourse` turns the
 * course title of a row into a filter for that course (not offered once a course is the filter);
 * it is the one way to filter by course for accounts that cannot list the courses.
 */
function DirectoryTable({
  rows, meta, now, courseId, onPickCourse,
}) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const columns = columnsFor(meta);

  return (
    <table className="ui-table sdir-table">
      <thead>
        <tr>
          <th className="sdir-th-student">{t('student360.directory.cols.student')}</th>
          {columns.contact && <th className="sdir-th-contact">{t('student360.directory.cols.contact')}</th>}
          {columns.learning && <th className="sdir-th-progress">{t('student360.directory.cols.progress')}</th>}
          {columns.lesson && <th className="sdir-th-lesson">{t('student360.directory.cols.lesson')}</th>}
          {columns.presence && <th className="sdir-th-seen">{t('student360.directory.cols.lastSeen')}</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <DirectoryRow
            key={row.id}
            row={row}
            columns={columns}
            now={now}
            lang={lang}
            detailPath={`/students/${row.id}${courseId ? `?courseId=${courseId}` : ''}`}
            onOpen={navigate}
            onPickCourse={courseId ? undefined : onPickCourse}
          />
        ))}
      </tbody>
    </table>
  );
}

export default DirectoryTable;
